import { SidebarEventLabels } from 'analytics/constants/labels';
import { matchPath } from 'react-router-dom';

import { PATHS } from 'constants/routerPaths';

import { CalendarIcon, DownloadApp, Guides, Meal, Settings, Workout } from 'components/Icon';

import { CUSTOM_WORKOUT_ACCESS, MEAL_PLAN_ACCESS } from './accessRules';
import { NavItem } from './types';

/** Any of these `/features` keys switches the workout-plan title to "Today". */
const TODAY_TITLE_FEATURES = ['nutrition_meal_suggestion'];

/**
 * Single, ordered, frozen navigation registry. Array order = sidebar order.
 * The sidebar, route guards, and active-page enforcement all read this list,
 * so they cannot disagree about which links exist or who may reach them.
 */
export const NAV_ITEMS: readonly NavItem[] = Object.freeze([
    {
        id: 'download-app',
        path: PATHS.DOWNLOAD_APP,
        icon: DownloadApp,
        dataLocator: 'burgerMuscleBoosterApp',
        analyticLabel: SidebarEventLabels.app,
        title: 'sidebar.download',
        access: { account: 'free' },
    },
    {
        id: 'workout-plan',
        path: PATHS.WORKOUT_PLAN,
        icon: CalendarIcon,
        dataLocator: 'workoutPlan',
        analyticLabel: SidebarEventLabels.workoutPlan,
        title: (access) =>
            TODAY_TITLE_FEATURES.some((feature) => access.features[feature]) ? 'sidebar.today' : 'sidebar.plan',
        access: { account: 'paid' },
    },
    {
        id: 'workout-builder',
        path: PATHS.WORKOUT_BUILDER,
        icon: Workout,
        dataLocator: 'workouts',
        analyticLabel: SidebarEventLabels.workout,
        title: 'sidebar.workout',
        access: CUSTOM_WORKOUT_ACCESS,
    },
    {
        id: 'meal-plan',
        path: PATHS.MEAL_PLAN,
        icon: Meal,
        dataLocator: 'mealPlan',
        analyticLabel: SidebarEventLabels.mealPlan,
        title: 'sidebar.mealPlan',
        access: MEAL_PLAN_ACCESS,
        // The single meal detail page is not its own sidebar link, but it gates
        // on the same rule — let the route guard and enforcement reach it here.
        matchPaths: [PATHS.MEAL_PAGE],
    },
    {
        id: 'fitness-guides',
        path: PATHS.FITNESS_GUIDES,
        icon: Guides,
        dataLocator: 'burgerGuides',
        analyticLabel: SidebarEventLabels.guides,
        title: 'sidebar.guides',
        access: { account: 'free' },
    },
    {
        id: 'account',
        path: PATHS.ACCOUNT_DEFAULT,
        icon: Settings,
        dataLocator: 'burgerAccountSettings',
        analyticLabel: SidebarEventLabels.account,
        title: 'sidebar.settings',
        access: { account: 'free' },
    },
]);

/**
 * Resolve the `NAV_ITEMS` entry that governs a live URL. Matches the concrete
 * `pathname` against each item's `path` and any `matchPaths` via `matchPath`, so
 * parameterized child routes (`/meals/123` → the meal-plan entry) resolve to the
 * rule that owns them. Used by route enforcement that runs against a real URL
 * (`useEnforceAccess`, `routeDependsOnFeatures`).
 */
export const findNavItemByPathname = (pathname: string): NavItem | undefined =>
    NAV_ITEMS.find((navItem) =>
        [navItem.path, ...(navItem.matchPaths ?? [])].some((pattern) => matchPath(pattern, pathname))
    );

/**
 * Resolve the `NAV_ITEMS` entry for a route *pattern* (e.g. `/meals/:mealId`),
 * not a live URL — so this is exact-equality against `path`/`matchPaths`, never
 * `matchPath`. Used by `canAccessNavRoute`, which is called at route-config time
 * with the pattern itself.
 */
export const findNavItemByRoutePattern = (pattern: string): NavItem | undefined =>
    NAV_ITEMS.find((navItem) => navItem.path === pattern || navItem.matchPaths?.includes(pattern));

/**
 * Does the route at `pathname` gate on `/features` or Eppo flags? Only such
 * routes must wait for nav-readiness before rendering — account-only routes
 * (settings, change email) don't, so they shouldn't sit behind a spinner while
 * `/features` is in flight. Mirrors the deferral in `canAccessRoute`: a gate
 * counts only when the governing `NAV_ITEMS` entry carries `feature`/`flag`.
 */
export const routeDependsOnFeatures = (pathname: string): boolean => {
    const item = findNavItemByPathname(pathname);

    return Boolean(item && (item.access.feature || item.access.flag));
};
